import { Link } from "@inertiajs/react"
import { TextInput } from "bonza-shared/js/components/atoms/formComponents/TextInput"
import { PrimaryButton } from "bonza-shared/js/components/atoms/Button/Button"
import { Controller, useForm } from "react-hook-form"
import {
    MarketingSignupRequest,
    submitEmailMarketing,
} from "bonza-shared/js/services/CRMService"
import { useEffect, useState } from "react"
import { emailRegex } from "bonza-shared/js/hooks/MiscHooks"
import { Alert } from "bonza-shared/js/components/atoms/formComponents/Alert"
import LoadingSpinner from "@/components/atoms/LoadingSpinner/loadingSpinner"
import { CustomCheckbox } from "bonza-shared/js"

export default function MarketingSignupInput({
    showWindowsToggle = false,
    toggleWindowsCheckbox = false,
    className,
}: {
    showWindowsToggle?: boolean
    toggleWindowsCheckbox?: boolean
    className?: string
}) {
    const [hasSignedUp, setHasSignedUp] = useState(false)
    const [resError, setResError] = useState("")
    const [isPending, setIsPending] = useState(false)
    const {
        register,
        handleSubmit,
        clearErrors,
        reset,
        control,
        setValue,
        formState: { errors },
    } = useForm<MarketingSignupRequest>({
        defaultValues: {
            email: "",
            interest_in_windows: toggleWindowsCheckbox,
        },
    })

    useEffect(() => {
        if (showWindowsToggle) setValue("interest_in_windows", true)
    }, [toggleWindowsCheckbox])

    const onSubmit = async (data: MarketingSignupRequest) => {
        console.log(data)
        setIsPending(true)
        setHasSignedUp(false)
        await submitEmailMarketing(data)
            .then((res) => {
                if (res.success) {
                    setHasSignedUp(true)
                    reset()
                    clearErrors()
                    setResError("")
                }
            })
            .catch((res) => {
                setResError(res.message)
            })
            .finally(() => setIsPending(false))
    }

    return (
        <form
            className={`
                flex flex-col gap-4

                ${className}
            `}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div
                className={`relative flex w-full flex-wrap justify-center gap-4`}
            >
                <TextInput
                    id="email"
                    className={`
                        w-0 min-w-[150px] grow rounded-full bg-bonza-dark-bg p-4
                    `}
                    placeholder={"email@example.com"}
                    register={register("email", {
                        required: "An email is required",
                        pattern: {
                            value: emailRegex,
                            message: "Email address is invalid",
                        },
                        onChange: () => {
                            clearErrors("email")
                        },
                    })}
                    disabled={isPending}
                />
                <PrimaryButton
                    className={"min-w-[110px] text-nowrap rounded-full"}
                    type="submit"
                    disabled={isPending}
                >
                    {isPending ? (
                        <LoadingSpinner
                            className={`size-4 text-bonza-primary`}
                        />
                    ) : (
                        "Sign up"
                    )}
                </PrimaryButton>
                <Alert
                    error={errors.email?.message}
                    className={`absolute -bottom-10`}
                />
                <Alert
                    error={errors.interest_in_windows?.message}
                    className={`absolute -bottom-10`}
                />
            </div>
            {showWindowsToggle && (
                <Controller
                    name="interest_in_windows"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                        <CustomCheckbox
                            checked={field.value ?? false}
                            onChange={(e) => {
                                field.onChange(e)
                            }}
                            label={"Notify me about Windows release"}
                        />
                    )}
                />
            )}
            {resError && <span className={"text-bonza-red"}>{resError}</span>}
            {hasSignedUp && (
                <span className={"text-green-600"}>
                    Sign up successful, thank you for your interest in Bonza!
                </span>
            )}
            <span className={`text-sm text-bonza-pale-t-60`}>
                By submitting your email you are consenting to receive emails
                from Bonza and agree with the{" "}
                <Link
                    href={route("web.legal", { tandc: 1 })}
                    className={`
                        text-bonza-primary

                        hover:text-bonza-pale
                    `}
                >
                    Terms & Conditions
                </Link>
            </span>
        </form>
    )
}
